import {useCallback, useEffect, useState} from 'react';
import {Page, Box} from '@shopify/polaris';
import classNames from 'classnames';
import {useI18n} from '@shopify/react-i18n';
import {useMonorail} from 'packages/monorail';
import {useSessionId} from '@web-utilities/session-id';
import {useLocalStorage} from '@web-utilities/local-storage';
import {NotFound} from 'components/NotFound';
import {OnboardingModal} from 'components/OnboardingModal';
import {useShopLocalesAndMarkets} from 'utilities/use-shop-locales-and-markets';
import {
  TranslatableResourceFormProvider,
  TranslatableResourceSearchProvider,
} from 'features/Translations/context';
import {TranslatableResourceType} from 'types';

import {useTranslatableResource} from '../../hooks';

import {
  FooterHelpLink,
  TopBar,
  TranslatableResourceForm,
  TranslatableResourceSideBar,
} from './components';
import styles from './TranslatableResourcePage.scss';

const scope = ['TranslatableResourcePage'];
const HIDE_ONBOARDING_MODAL_STORAGE_KEY =
  'hide_translate_and_adapt_onboarding_modal';

export function TranslatableResourcePage() {
  const [i18n] = useI18n();
  const [{value: hideOnboardingModal, update: storeHideOnboardingModal}] =
    useLocalStorage<string>(HIDE_ONBOARDING_MODAL_STORAGE_KEY);

  const [onBoardingModalOpen, setOnboardingModalOpen] =
    useState(!hideOnboardingModal);
  const [sidebarHasNoResults, setSidebarHasNoResults] = useState<
    null | boolean
  >(null);
  const {
    resourceId,
    resourceType,
    initialLoading,
    resourceDisplayTitle,
    translatableResourcesQueryLoading,
  } = useTranslatableResource();
  const {
    loading: localesAndMarketsLoading,
    currentLocale,
    primaryLocale,
    currentMarket,
  } = useShopLocalesAndMarkets();
  const monorail = useMonorail();
  const sessionId = useSessionId();

  const loading = initialLoading || localesAndMarketsLoading;
  const lowerCaseResourceType = resourceType.toLowerCase();
  const hideMarketSelector =
    resourceType === TranslatableResourceType.PackingSlipTemplate;

  const produceViewedActionMonorailEvent = useCallback(() => {
    if (!currentLocale) return;
    monorail.produce({
      schemaId: 'resource_translations_event/1.2',
      payload: {
        eventType: 'translation_viewed',
        resourceLocale: currentLocale.locale,
        resourceName: resourceDisplayTitle,
        resourceType: lowerCaseResourceType,
        translationUiType: 'side by side',
        sessionToken: sessionId,
      },
    });
  }, [
    currentLocale,
    lowerCaseResourceType,
    monorail,
    resourceDisplayTitle,
    sessionId,
  ]);

  useEffect(() => {
    setSidebarHasNoResults(null);
  }, [resourceType]);

  useEffect(() => {
    if (resourceDisplayTitle) {
      produceViewedActionMonorailEvent();
    }
  }, [produceViewedActionMonorailEvent, resourceDisplayTitle]);

  if (!resourceId && resourceType === TranslatableResourceType.Shop) {
    return <NotFound />;
  }

  const exitActionMonorailEvent = () => {
    if (!currentLocale) return;
    monorail.produce({
      schemaId: 'resource_translations_event/1.2',
      payload: {
        eventType: 'exit_translation_ui',
        resourceLocale: currentLocale.locale,
        resourceName: resourceDisplayTitle,
        resourceType: lowerCaseResourceType,
        translationUiType: 'side by side',
        sessionToken: sessionId,
      },
    });
  };

  const translatedContentLocaleName = currentMarket
    ? `${currentLocale?.name} (${currentMarket.name})`
    : currentLocale?.name;
  const defaultContentLocaleName = currentMarket
    ? i18n.translate(
        'defaultLanguage',
        {scope},
        {
          language: currentLocale?.name || '',
        },
      )
    : primaryLocale?.name;

  const translatableResourceForm = (
    <div className={styles.FormWrapper}>
      <Box padding={{xs: '0', sm: '400'}} paddingBlockEnd="0">
        <TranslatableResourceForm
          translatedContentLocaleName={translatedContentLocaleName || ''}
          defaultContentLocaleName={defaultContentLocaleName || ''}
          loading={loading}
        />
      </Box>
    </div>
  );

  const translatableResourceSideBar = (
    <>
      <TranslatableResourceSideBar
        loading={loading}
        onFetchCompleted={(itemsCount) => {
          setSidebarHasNoResults(itemsCount === 0);
        }}
      />
      <div className={styles.MobileFooterHelpWrapper}>
        <FooterHelpLink />
      </div>
    </>
  );

  const sidebarMarkup = (
    <div className={styles.SidebarWrapper}>{translatableResourceSideBar}</div>
  );

  return (
    <TranslatableResourceSearchProvider>
      <TranslatableResourceFormProvider>
        <div className={styles.TopBar}>
          <TopBar
            loading={
              initialLoading ||
              translatableResourcesQueryLoading ||
              localesAndMarketsLoading
            }
            sidebarHasNoResults={sidebarHasNoResults}
            exitAction={exitActionMonorailEvent}
            hideMarketSelector={hideMarketSelector}
          />
        </div>
        <div className={styles.PageWrapper}>
          <Page fullWidth>
            <div
              className={classNames(
                styles.PageContentWrapper,
                styles.PageContentWrapper__ResponsiveGrid,
              )}
            >
              {sidebarMarkup}
              {translatableResourceForm}
            </div>
            <OnboardingModal
              open={onBoardingModalOpen}
              onClose={() => {
                setOnboardingModalOpen(false);
                storeHideOnboardingModal('true');
              }}
            />
          </Page>
        </div>
      </TranslatableResourceFormProvider>
    </TranslatableResourceSearchProvider>
  );
}
